import { create } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { checklistServiceClient } from "@/connect";
import type {
  ChecklistTemplate,
  CreateChecklistTemplateRequest,
  ListChecklistTemplatesRequest,
} from "@/types/proto/api/v1/checklist_service_pb";
import {
  ChecklistTemplateSchema,
  CreateChecklistTemplateRequestSchema,
  ListChecklistTemplatesRequestSchema,
} from "@/types/proto/api/v1/checklist_service_pb";

// Query keys factory for consistent cache management
export const checklistKeys = {
  all: ["checklists"] as const,
  lists: () => [...checklistKeys.all, "list"] as const,
  list: (filters: Partial<ListChecklistTemplatesRequest>) => [...checklistKeys.lists(), filters] as const,
  details: () => [...checklistKeys.all, "detail"] as const,
  detail: (uid: string) => [...checklistKeys.details(), uid] as const,
};

/**
 * Hook to list checklist templates
 */
export function useChecklistTemplates(request: Partial<ListChecklistTemplatesRequest> = {}) {
  return useQuery({
    queryKey: checklistKeys.list(request),
    queryFn: async () => {
      const response = await checklistServiceClient.listChecklistTemplates(
        create(ListChecklistTemplatesRequestSchema, request as Record<string, unknown>),
      );
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - templates change infrequently
  });
}

/**
 * Hook to get a single checklist template by UID
 */
export function useChecklistTemplate(uid: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: checklistKeys.detail(uid),
    queryFn: async () => {
      const template = await checklistServiceClient.getChecklistTemplate({ uid });
      return template;
    },
    enabled: options?.enabled ?? !!uid,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a new checklist template
 */
export function useCreateChecklistTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: Partial<CreateChecklistTemplateRequest>) => {
      const template = await checklistServiceClient.createChecklistTemplate(
        create(CreateChecklistTemplateRequestSchema, request as Record<string, unknown>),
      );
      return template;
    },
    onSuccess: () => {
      // Invalidate all checklist queries
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() });
    },
  });
}

/**
 * Hook to update a checklist template
 */
export function useUpdateChecklistTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ template, updateMask }: { template: Partial<ChecklistTemplate>; updateMask?: string[] }) => {
      const updated = await checklistServiceClient.updateChecklistTemplate({
        template: create(ChecklistTemplateSchema, template as Record<string, unknown>),
        updateMask: updateMask ? { paths: updateMask } : undefined,
      });
      return updated;
    },
    onSuccess: (data) => {
      // Invalidate specific template and list queries
      const uid = data.uid;
      if (uid) {
        queryClient.invalidateQueries({ queryKey: checklistKeys.detail(uid) });
      }
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() });
    },
  });
}

/**
 * Hook to delete a checklist template
 */
export function useDeleteChecklistTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uid: string) => {
      await checklistServiceClient.deleteChecklistTemplate({ uid });
      return uid;
    },
    onSuccess: (uid) => {
      // Invalidate specific template and list queries
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(uid) });
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() });
    },
  });
}
